type SentryFrameLike = {
  filename?: string | null;
  function?: string | null;
};

type SentryExceptionLike = {
  type?: string | null;
  value?: string | null;
  stacktrace?: {
    frames?: SentryFrameLike[] | null;
  } | null;
};

type SentryEventLike = {
  exception?: {
    values?: SentryExceptionLike[] | null;
  } | null;
  tags?: Record<string, unknown> | null;
  contexts?: Record<string, unknown> | null;
};

const META_IN_APP_BROWSERS = [
  "facebook",
  "facebook mobile",
  "instagram",
  "messenger",
  "threads",
];

const INJECTED_AUTH_BRIDGE_FUNCTIONS = new Set([
  "dispatchToBridge",
  "sendPostMessage",
  "logLoginFieldDetected",
  "mutationObserverCallback",
]);

const INVALID_ACCESS_BRIDGE_MESSAGE =
  "the object does not support the operation or argument";

// Chrome rejects Clipboard API reads when the tab is not focused (Cypress
// `win.open` tab hops, password managers, in-page eval). The app never calls
// `clipboard.readText()`, so a stackless NotAllowedError is injected noise.
// Do not match `writeText`: Chrome's Clipboard DOMException stack is natively
// anonymous, so first-party copy failures (a copy made after an
// awaited network call, install-script copy, etc.) look identical to injected
// noise and would otherwise be dropped in `beforeSend`.
const CLIPBOARD_UNFOCUSED_MESSAGE =
  /failed to execute 'readtext' on 'clipboard': document is not focused/;

// Safari (and other WebKit browsers) surface a failed <script type="module">
// or dynamic import() as this TypeError with no frames. Chrome includes the
// module URL and is kept — that form is more often a stale hashed chunk.
const MODULE_SCRIPT_IMPORT_FAILED = "importing a module script failed";

// Maps User-Agent substrings to the canonical Meta in-app browser name so the
// guard works on the client (in beforeSend), where Sentry has not yet inferred
// browser.name/contexts.browser from the UA — that only happens server-side at
// ingest, so client events almost never carry those fields.
const META_UA_SIGNATURES: ReadonlyArray<readonly [RegExp, string]> = [
  [/instagram/i, "instagram"],
  [/messenger|fb_iab\/messenger|orca-android/i, "messenger"],
  [/barcelona/i, "threads"], // Threads' in-app browser identifies as "Barcelona".
  [/fban|fbav|fb_iab|fbios|fb4a/i, "facebook"],
];

const getUserAgentBrowserName = () => {
  const userAgent =
    typeof navigator !== "undefined" && typeof navigator.userAgent === "string"
      ? navigator.userAgent
      : "";
  if (!userAgent) return "";

  for (const [pattern, name] of META_UA_SIGNATURES) {
    if (pattern.test(userAgent)) return name;
  }
  return "";
};

const getBrowserName = (event: SentryEventLike) => {
  const tagValue = event.tags?.["browser.name"];
  if (typeof tagValue === "string" && tagValue) return tagValue;

  const browserContext = event.contexts?.browser;
  if (browserContext && typeof browserContext === "object") {
    const browser = browserContext as Record<string, unknown>;
    const contextName = browser.name ?? browser.browser;
    if (typeof contextName === "string" && contextName) return contextName;
  }

  // Fall back to the raw User-Agent, the only signal available client-side.
  return getUserAgentBrowserName();
};

const getFrames = (exception: SentryExceptionLike) =>
  exception.stacktrace?.frames?.filter(Boolean) ?? [];

const hasConcreteSourceFile = (frame: SentryFrameLike) => {
  const filename = frame.filename?.trim();
  if (!filename) return false;

  return (
    filename.endsWith(".js") ||
    filename.endsWith(".mjs") ||
    filename.endsWith(".ts") ||
    filename.endsWith(".tsx") ||
    filename.includes("/assets/") ||
    filename.includes("/src/") ||
    filename.startsWith("webpack://") ||
    filename.startsWith("vite://")
  );
};

export const isMetaInAppBrowserInjectedAuthBridgeError = (event: SentryEventLike) => {
  const browserName = getBrowserName(event).toLowerCase();
  if (!META_IN_APP_BROWSERS.some((browser) => browserName.includes(browser))) {
    return false;
  }

  const exceptions = event.exception?.values ?? [];
  return exceptions.some((exception) => {
    const exceptionType = exception.type?.toLowerCase() ?? "";
    const exceptionValue = exception.value?.toLowerCase() ?? "";
    if (
      exceptionType !== "invalidaccesserror" ||
      !exceptionValue.includes(INVALID_ACCESS_BRIDGE_MESSAGE)
    ) {
      return false;
    }

    const frames = getFrames(exception);
    const functionNames = frames.map((frame) => frame.function).filter(Boolean);
    const hasInjectedBridgeFrame = functionNames.some((name) =>
      INJECTED_AUTH_BRIDGE_FUNCTIONS.has(name as string),
    );

    return hasInjectedBridgeFrame && !frames.some(hasConcreteSourceFile);
  });
};

export const isUnfocusedClipboardAccessError = (event: SentryEventLike) => {
  const exceptions = event.exception?.values ?? [];
  return exceptions.some((exception) => {
    const exceptionType = exception.type?.toLowerCase() ?? "";
    const exceptionValue = exception.value?.toLowerCase() ?? "";
    if (
      exceptionType !== "notallowederror" ||
      !CLIPBOARD_UNFOCUSED_MESSAGE.test(exceptionValue)
    ) {
      return false;
    }

    const frames = getFrames(exception);
    return !frames.some(hasConcreteSourceFile);
  });
};

export const isStacklessModuleScriptImportError = (event: SentryEventLike) => {
  const exceptions = event.exception?.values ?? [];
  return exceptions.some((exception) => {
    const exceptionType = exception.type?.toLowerCase() ?? "";
    const exceptionValue = exception.value?.toLowerCase() ?? "";
    if (
      exceptionType !== "typeerror" ||
      !exceptionValue.includes(MODULE_SCRIPT_IMPORT_FAILED)
    ) {
      return false;
    }

    const frames = getFrames(exception);
    return !frames.some(hasConcreteSourceFile);
  });
};

const BROWSER_NOISE_CHECKS = [
  isMetaInAppBrowserInjectedAuthBridgeError,
  isUnfocusedClipboardAccessError,
  isStacklessModuleScriptImportError,
] as const;

/** Single `beforeSend` gate — every injected-browser drop lives here. */
export const isBrowserNoise = (event: SentryEventLike) =>
  BROWSER_NOISE_CHECKS.some((check) => check(event));
